import EnergySavingCTA from "./components/EnergySavingCTA/EnergySavingCTA";
import { useCallback, useRef, useState } from "react";
import Script from "next/script";
import styles from "./EnergySaving.module.scss";
import { YourekoMedal } from "@/components/EnergySaving/useYourekoLabels.ts";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";

const yourekoScriptUrl =
  "https://static.youreko.com/js/partners/gb/john-lewis/youreko.energy-review.john-lewis.no-hover-intent.all.min.js";

type Props = {
  energyCostSavingUnformatted: number;
  numberOfAlternatives: number;
  medalRatingText: YourekoMedal;
  refetch: () => void;
};

export type EnergyLabelInputValues = {
  [index: string]: number | null;
  customUsageTimesPerPeriod: number | null;
  customUsagePeriod: number | null;
  customEnergyCostYears: number | null;
  customTariff: number | null;
};

declare global {
  interface Window {
    Youreko?: {
      EnergySavingsModal?: {
        show: () => void;
      };
    };
  }
}

const openYourekoModal = () => {
  window.Youreko?.EnergySavingsModal?.show?.();
};

export const EnergySavingUI = ({
  energyCostSavingUnformatted,
  numberOfAlternatives,
  medalRatingText,
  refetch,
}: Props) => {
  const [scriptInjected, setScriptInjected] = useState(false);

  const observerRef = useRef<MutationObserver | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (node === containerRef.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    containerRef.current = node;
    if (!node) return;

    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (
          m.type === "attributes" &&
          m.attributeName === "data-youreko-clicked"
        ) {
          const el = m.target;
          if (
            el instanceof Element &&
            !el.hasAttribute("data-youreko-clicked")
          ) {
            refetch();
          }
        }
      }
    });

    obs.observe(node, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-youreko-clicked"],
      attributeOldValue: true,
    });

    observerRef.current = obs;
  }, []);

  const handleOpenModal = () => {
    dispatchMonetateQEvent("standard-pdp-energy-saving-open-modal");

    if (!scriptInjected) {
      setScriptInjected(true);
    } else {
      openYourekoModal();
    }
  };

  return (
    <>
      {scriptInjected && (
        <Script src={yourekoScriptUrl} onLoad={openYourekoModal} />
      )}
      <div data-component="EnergySaving" className={styles.yourekoContainer}>
        <EnergySavingCTA
          onClick={handleOpenModal}
          energyCostSavingUnformatted={energyCostSavingUnformatted}
          medalRatingText={medalRatingText}
          numberOfAlternatives={numberOfAlternatives}
        />

        <div
          className={styles.yourekoHiddenProxyContainer}
          aria-hidden="true"
          ref={setContainerRef}
        >
          <div className="youreko-energy-label" data-youreko-clicked={true} />
        </div>
      </div>
    </>
  );
};
